/**
 * LoRA API Service
 *
 * Wraps /components/deforum/loras and /civitai/search/loras endpoints.
 * RULE #16: uses getApiBaseUrl() + httpUtils.JSON_HEADERS exclusively.
 */

import { getApiBaseUrl } from './urlResolver';
import { JSON_HEADERS, handleDirectResponse } from '../utils/httpUtils';

// ============================================================================
// Types
// ============================================================================

export interface LoraInfo {
  slug: string;
  filename: string;
  display_name: string;
  base_model_compat: 'SD 1.5' | 'SDXL 1.0';
  size_mb: number;
  is_hyper_speed_lora: boolean;
  hyper_steps: number | null;
  trigger_words: string[];
  recommended_strength: number;
  is_installed: boolean;
  description: string;
  creator: string;
  license_summary: string;
}

export interface CivitAILoraSearchItem {
  id: number;
  name: string;
  base_model: string;
  trigger_words: string[];
  thumbnail_url: string | null;
  downloads_count: number;
  rating: number | null;
  license_label: string;
  size_mb: number;
  creator: string;
  nsfw: boolean;
  latest_version_id: number;
}

export interface LoraInstallJobStatus {
  status: 'queued' | 'downloading' | 'verifying' | 'installed' | 'failed';
  progress_mb: number;
  total_mb: number;
  error: string | null;
}

export interface CivitAILoraSearchResult {
  items: CivitAILoraSearchItem[];
  next_cursor: string | null;
}

export interface LoraSearchOpts {
  query?: string;
  base_model?: string;
  cursor?: string;
  nsfw?: boolean;
}

export interface LoraInstallOpts {
  slug?: string;
  civitai_version_id?: number;
}

// ============================================================================
// API calls
// ============================================================================

/**
 * GET /api/v1/components/deforum/loras
 * Returns list of installed LoRAs.
 */
export async function listInstalledLoras(): Promise<LoraInfo[]> {
  const base = getApiBaseUrl();
  const res = await fetch(`${base}/api/v1/components/deforum/loras`, {
    headers: JSON_HEADERS,
  });
  return handleDirectResponse<LoraInfo[]>(res);
}

/**
 * GET /api/v1/civitai/search/loras
 * Searches CivitAI LoRA catalog.
 */
export async function searchCivitAILoras(
  opts: LoraSearchOpts = {}
): Promise<CivitAILoraSearchResult> {
  const base = getApiBaseUrl();
  const params = new URLSearchParams();
  if (opts.query) params.set('query', opts.query);
  if (opts.base_model) params.set('base_model', opts.base_model);
  if (opts.cursor) params.set('cursor', opts.cursor);
  if (opts.nsfw !== undefined) params.set('nsfw', String(opts.nsfw));
  const qs = params.toString();
  const url = `${base}/api/v1/civitai/search/loras${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: JSON_HEADERS });
  return handleDirectResponse<CivitAILoraSearchResult>(res);
}

/**
 * POST /api/v1/components/deforum/loras/install
 * Starts an async install job. Returns {job_id}.
 */
export async function installLora(opts: LoraInstallOpts): Promise<{ job_id: string }> {
  const base = getApiBaseUrl();
  const res = await fetch(`${base}/api/v1/components/deforum/loras/install`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(opts),
  });
  return handleDirectResponse<{ job_id: string }>(res);
}

/**
 * GET /api/v1/components/deforum/loras/install/{job_id}/status
 * Polls install job status.
 *
 * The `/status` suffix is part of the backend route
 * (api_server/local_routes/components/deforum_models.py::get_lora_install_status).
 * Omitting it 404s forever and the progress bar never advances.
 */
export async function getLoraInstallStatus(job_id: string): Promise<LoraInstallJobStatus> {
  const base = getApiBaseUrl();
  const res = await fetch(
    `${base}/api/v1/components/deforum/loras/install/${encodeURIComponent(job_id)}/status`,
    { headers: JSON_HEADERS }
  );
  return handleDirectResponse<LoraInstallJobStatus>(res);
}
